<template>
  <h2 class="font-semibold text-grey-light-900">Plan Details</h2>
  <div class="flex justify-between text-grey-light-700">
    <span>Product Name</span>
    <span>{{ order.name }}</span>
  </div>
  <div class="flex justify-between text-grey-light-700">
    <span>Order Reference</span>
    <span>{{ order.reference }}</span>
  </div>

  <div class="h-[1px] w-full my-4 bg-grey-light-200" />

  <div class="flex justify-between text-grey-light-700">
    <span>Total</span>
    <span class="text-grey-light-900 font-semibold">{{ displayCurrency(order.amountDue) }}</span>
  </div>
  <div class="flex justify-between text-grey-light-700">
    <span>Total Deposit</span>
    <span class="text-grey-light-900 font-semibold text-xl">{{ displayCurrency(order.depositAmount) }}</span>
  </div>
  <div class="flex justify-between text-grey-light-700">
    <span>Final Payment Date</span>
    <span class="text-brand-700">{{ formatDateShort(endDate) }}</span>
  </div>
  <div class="flex justify-between text-grey-light-700" v-if="useTravelDate">
    <span>Travel Date</span>
    <span class="text-brand-700">{{ formatDateShort(travelDate) }}</span>
  </div>

  <div class="flex flex-col gap-2 border border-grey-light-200 rounded-lg bg-grey-light-50 p-3">
    <span class="text-grey-light-700 font-medium">Payment plan breakdown</span>
    <span v-if="!loadingPreview" class="text-grey-light-900 font-semibold">{{ previewText }}</span>
    <div v-else class="flex justify-center">
      <LayupSpinner />
    </div>
  </div>

  <div class="h-[1px] w-full my-4 bg-grey-light-200"></div>

  <div class="grid grid-cols-4 gap-4">
    <div class="col-span-4">
      <div class="flex gap-2 justify-between items-center">
        <LabelComponent>Allow order cloning</LabelComponent>
        <SwitchComponent v-model="allowOrderCloning" class="!mt-0" />
      </div>
    </div>
    <template v-if="allowOrderCloning">
      <div class="col-span-2">
        <Input v-model="expiryTime" type="text" @input="validateExpiryTime" placeholder="Enter Value" />
        <span v-if="cloningErrors.cloningExpiryDuration !== ''" class="text-sm font-medium text-destructive">
          {{ cloningErrors.cloningExpiryDuration }}
        </span>
      </div>
      <div class="col-span-2">
        <Select v-model="expiryType">
          <SelectTrigger>
            <SelectValue placeholder="Select the unit of time" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="minutes">Minutes</SelectItem>
              <SelectItem value="hours">Hours</SelectItem>
              <SelectItem value="days">Days</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div class="col-span-4">
        <div class="flex gap-2 justify-between items-center">
          <LabelComponent>Order Limit</LabelComponent>
          <SwitchComponent v-model="orderLimitEnabled" class="!mt-0" />
        </div>
      </div>
      <template v-if="orderLimitEnabled">
        <div class="col-span-2">
          <Input v-model="orderLimit" type="text" @input="validateOrderLimit" placeholder="Enter Value" />
          <span v-if="cloningErrors.cloningLimit !== ''" class="text-sm font-medium text-destructive">
            {{ cloningErrors.cloningLimit }}
          </span>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import type { CloningErrors } from '@/models/orders';
import { generatePaymentPlanPreview } from '@/services/paymentPlans';
import { isNumber } from '@/utils/form.utils';
import { displayCurrency, formatDateShort } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import type {
  ModelGroupPayment,
  ModelOrderResponse,
  ModelPaymentPlanPreviewRequest,
  ModelPaymentPlanPreviewResponseMany,
} from '@layup/api-sdk';

const props = defineProps<{
  order: Partial<ModelOrderResponse>;
  endDate: string;
  travelDate: string;
  useTravelDate: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:can-create-plan', value: boolean): void;
  (e: 'update:error', value: string): void;
}>();

const previewText = ref('');
const loadingPreview = ref(false);
const allowOrderCloning = ref(false);
const expiryTime = ref('');
const expiryType = ref<'minutes' | 'hours' | 'days'>('minutes');
const orderLimitEnabled = ref(false);
const orderLimit = ref('');

const groupPaymentPlan = reactive<ModelGroupPayment>({ enabled: true, expiry: 0, cloneLimit: 0 });

const cloningErrors = reactive<CloningErrors>({
  cloningExpiryDuration: '',
  cloningExpiryTimeUnit: '',
  cloningLimit: '',
});

const validateAndEmitCanCreatePlan = () => {
  let isValid = true;

  if (allowOrderCloning.value) {
    if (!expiryTime.value || !expiryType.value || cloningErrors.cloningExpiryDuration !== '') {
      isValid = false;
    }

    if (orderLimitEnabled.value) {
      if (!orderLimit.value || cloningErrors.cloningLimit !== '') {
        isValid = false;
      }
    }
  }

  emit('update:can-create-plan', isValid);
};

const resetGroupPaymentPlan = () => {
  expiryTime.value = '';
  orderLimit.value = '';
  groupPaymentPlan.expiry = 0;
  groupPaymentPlan.cloneLimit = 0;
};

onMounted(async () => {
  try {
    loadingPreview.value = true;

    const orderCopy: ModelPaymentPlanPreviewRequest = {
      amountDue: props.order.amountDue,
      depositPerc: props.order.depositPerc,
      depositAmount: props.order.depositAmount,
      depositType: props.order.depositType,
      endDateMax: props.endDate,
      endDateMin: props.order.endDateMin,
      percFee: props.order.percFee,
      flatFee: props.order.flatFee,
      hideBillingDate: props.order.hideBillingDate,
    };

    if (orderCopy.depositType !== 'FLAT') {
      delete orderCopy.depositAmount;
    }

    const preview = await generatePaymentPlanPreview(orderCopy);
    buildPaymentBreakdown(preview);
  } catch (error: any) {
    if (error.response?.data) {
      const serverMessage = error.response?.data || 'Unknown error';
      emit('update:error', serverMessage as string);
    }

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create-payment-plan-preview', {
        status: 'network_error',
        channel: 'merchant',
        method: 'generatePaymentPlanPreview',
      });
    } else {
      createException(error);
      addMetric('create-payment-plan-preview', {
        status: 'error',
        channel: 'merchant',
        method: 'generatePaymentPlanPreview',
      });
    }
  } finally {
    loadingPreview.value = false;
    validateAndEmitCanCreatePlan();
  }
});

onUnmounted(() => emit('update:error', ''));

watch([() => expiryType.value, () => expiryTime.value], ([newType, newTime]) => {
  if (newType && newTime) {
    const timeValue = Number(newTime);

    if (newType === 'minutes') {
      if (timeValue >= 0 && timeValue <= 86400) {
        groupPaymentPlan.expiry = timeValue;
        cloningErrors.cloningExpiryDuration = '';
      } else {
        cloningErrors.cloningExpiryDuration = `Value can't be more than 86400 (60 days)`;
      }
    }

    if (newType === 'hours') {
      if (timeValue >= 0 && timeValue <= 1440) {
        groupPaymentPlan.expiry = timeValue * 60;
        cloningErrors.cloningExpiryDuration = '';
      } else {
        cloningErrors.cloningExpiryDuration = `Value can't be more than 1440 (60 days)`;
      }
    }

    if (newType === 'days') {
      if (timeValue >= 0 && timeValue <= 60) {
        groupPaymentPlan.expiry = timeValue * 24 * 60;
        cloningErrors.cloningExpiryDuration = '';
      } else {
        cloningErrors.cloningExpiryDuration = `Value can't be more than 60`;
      }
    }

    props.order.groupPaymentPlan = groupPaymentPlan;

    validateAndEmitCanCreatePlan();
  }
});

watch(
  () => orderLimit.value,
  (newValue) => {
    if (newValue) {
      const limitValue = Number(newValue);
      if (limitValue > 0) {
        groupPaymentPlan.cloneLimit = limitValue;
        cloningErrors.cloningLimit = '';
      } else {
        cloningErrors.cloningLimit = 'Order limit must be greater than 0';
      }
    } else if (orderLimitEnabled.value) {
      cloningErrors.cloningLimit = 'Order limit is required when enabled';
    }

    validateAndEmitCanCreatePlan();
  },
);

watch(
  () => allowOrderCloning.value,
  (isEnabled) => {
    if (!isEnabled) {
      resetGroupPaymentPlan();
    }
    validateAndEmitCanCreatePlan();
  },
);

watch(
  () => orderLimitEnabled.value,
  (isEnabled) => {
    if (!isEnabled) {
      groupPaymentPlan.cloneLimit = 0;
    }
    validateAndEmitCanCreatePlan();
  },
);

const buildPaymentBreakdown = (preview: ModelPaymentPlanPreviewResponseMany) => {
  if (preview.paymentPlans) {
    // Payment plans array is a collection of options for the order at each month (1month, 2mo....)
    // Thats why the last one is picked because its the full plan
    const plan = preview.paymentPlans[preview.paymentPlans.length - 1];
    const amount = plan.payments ? plan.payments[plan.payments.length - 1].amount || 0 : 0;
    const months = plan.months;
    previewText.value = `${displayCurrency(amount)}/pm for ${months} months`;
  }
};

const validateExpiryTime = (event: Event) => {
  const inputValue = isNumber(event);
  expiryTime.value = inputValue;
};
const validateOrderLimit = (event: Event) => {
  const inputValue = isNumber(event);
  orderLimit.value = inputValue;
};
</script>
